import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { PageHeader } from '@/components/organisms/PageHeader';
import { Footer } from '@/components/organisms/Footer';
import { LandingPageSkeleton } from '@/components/organisms/Loading';
import { logPageView, logButtonClick } from '@/services/analytics.service';
import { stats } from '@/data/stats';
import { LoginButton } from '@/components/molecules/LoginButton';
import { DemoCanvas } from '@/components/organisms/DemoCanvas';
import { getBoard, getBoardPixels } from '@/services/board.service';
import { Board, Pixel } from '@/types';

interface FeaturedBoard {
  board: Board | null;
  pixels: (Pixel | null)[][] | null;
  loading: boolean;
}

const FEATURED_BOARD_IDS: Record<string, string> = {
  sunset: '6HRzhKEAloMnqZy7RGCm',
  fanta: '2ivNyQWxFGdxCbTVeCLM',
  oddbit: 'XV8A8WADpU3FuvFKoYQH',
};

export const LandingPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [featuredBoards, setFeaturedBoards] = useState<Record<string, FeaturedBoard>>({});
  const canvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});

  useEffect(() => {
    logPageView('Landing Page');
  }, []);

  const renderBoardPreview = useCallback((board: Board, pixels: (Pixel | null)[][], boardName: string) => {
    const canvas = canvasRefs.current[boardName];
    if (!canvas) return;

    // Set canvas to actual board dimensions (1:1 scale like BoardCard)
    canvas.width = board.width;
    canvas.height = board.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Disable image smoothing for crisp pixel art
    ctx.imageSmoothingEnabled = false;

    // Fill background
    ctx.fillStyle = '#101322';
    ctx.fillRect(0, 0, board.width, board.height);

    // Draw pixels at 1:1 scale (each pixel is 1 canvas pixel)
    for (let y = 0; y < board.height; y++) {
      for (let x = 0; x < board.width; x++) {
        const pixel = pixels[y]?.[x];
        if (pixel) {
          ctx.fillStyle = pixel.color;
          ctx.fillRect(x, y, 1, 1);
        }
      }
    }
  }, []);

  useEffect(() => {
    const fetchFeaturedBoards = async () => {
      const boardsData: Record<string, FeaturedBoard> = {};
      
      for (const boardName of Object.keys(FEATURED_BOARD_IDS)) {
        boardsData[boardName] = { board: null, pixels: null, loading: true };
      }
      
      setFeaturedBoards(boardsData);

      for (const [boardName, boardId] of Object.entries(FEATURED_BOARD_IDS)) {
        try {
          const board = await getBoard(boardId);
          if (board) {
            const pixels = await getBoardPixels(board.id);
            setFeaturedBoards(prev => ({
              ...prev,
              [boardName]: { board, pixels, loading: false }
            }));
            
            // Render preview if canvas is already mounted
            const canvas = canvasRefs.current[boardName];
            if (canvas) {
              renderBoardPreview(board, pixels, boardName);
            }
          } else {
            setFeaturedBoards(prev => ({
              ...prev,
              [boardName]: { board: null, pixels: null, loading: false }
            }));
          }
        } catch (error) {
          console.error(`Error fetching board ${boardName}:`, error);
          setFeaturedBoards(prev => ({
            ...prev,
            [boardName]: { board: null, pixels: null, loading: false }
          }));
        }
      }
    };

    fetchFeaturedBoards();
  }, [renderBoardPreview]);

  const handleExploreAsGuest = () => {
    logButtonClick('Explore as Guest', 'Landing Page');
    navigate('/browse');
  };

  const handleViewMyBoards = () => {
    logButtonClick('View My Boards', 'Landing Page');
    if (user?.username) {
      navigate(`/user/${user.username}`);
    }
  };

  const handleCreateSketchBoard = () => {
    logButtonClick('Create Sketch Board', 'Landing Page');
    // Navigate to sketch page - it will create a fresh in-memory sketch board
    navigate('/sketch');
  };

  if (authLoading) {
    return <LandingPageSkeleton />;
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background text-foreground font-['Space_Grotesk',sans-serif] overflow-x-hidden">
      <PageHeader
        title="tuval.space"
        variant="landing"
        onLogoClick={() => navigate('/')}
      />

      <main className="flex-1 flex flex-col items-center w-full">
        {/* Hero Section */}
        <div className="w-full px-4 py-12 md:py-20 max-w-[1200px]">
          <div className="flex flex-col-reverse gap-10 lg:flex-row lg:items-center">
            {/* Hero Content */}
            <div className="flex flex-col gap-6 lg:w-1/2 justify-center">
              <div className="flex flex-col gap-4 text-left">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight bg-gradient-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent dark:from-white dark:via-slate-300 dark:to-slate-400">
                  {user
                    ? 'Welcome back!'
                    : 'Create Together on Shared Canvases'}
                </h1>
                <h2 className="text-muted-foreground text-base md:text-lg font-normal leading-relaxed max-w-[504px]">
                  {user
                    ? 'Continue your creative journey. Place pixels, explore shared canvases, and collaborate with the community.'
                    : 'A shared canvas where you can have fun together with others or use it as your daily creative journal. Place pixels, express yourself, and be part of a collaborative art experience.'}
                </h2>
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                {user ? (
                  <>
                    <button
                      onClick={handleViewMyBoards}
                      className="flex min-w-[140px] cursor-pointer items-center justify-center gap-2 rounded-lg h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-bold transition-all"
                    >
                      <span className="material-symbols-outlined text-lg">
                        dashboard
                      </span>
                      <span className="truncate">View My Boards</span>
                    </button>
                    <button
                      onClick={handleCreateSketchBoard}
                      className="flex min-w-[140px] cursor-pointer items-center justify-center gap-2 rounded-lg h-12 px-6 bg-card border border-border hover:border-primary text-foreground text-base font-bold transition-all"
                    >
                      <span className="material-symbols-outlined text-lg">
                        edit
                      </span>
                      <span className="truncate">Eskiz Board</span>
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex min-w-[140px] items-center justify-center">
                      <LoginButton />
                    </div>
                    <button
                      onClick={handleExploreAsGuest}
                      className="flex min-w-[140px] cursor-pointer items-center justify-center rounded-lg h-12 px-6 bg-card border border-border hover:border-primary text-foreground text-base font-bold transition-all"
                    >
                      <span className="truncate">Explore as Guest</span>
                    </button>
                    <button
                      onClick={handleCreateSketchBoard}
                      className="flex min-w-[140px] cursor-pointer items-center justify-center gap-2 rounded-lg h-12 px-6 bg-card border border-border hover:border-primary text-foreground text-base font-bold transition-all"
                    >
                      <span className="material-symbols-outlined text-lg">
                        edit
                      </span>
                      <span className="truncate">Eskiz Board</span>
                    </button>
                  </>
                )}
              </div>
              {/* Trust Indicators */}
              {!user && (
                <div className="flex items-center gap-3 pt-4 opacity-80">
                  <p className="text-sm font-medium text-muted-foreground">
                    Join 12,000+ artists online
                  </p>
                </div>
              )}
            </div>
            {/* Hero Visual */}
            <div className="lg:w-1/2 w-full flex justify-center">
              <DemoCanvas />
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="w-full border-y border-border">
          <div className="max-w-[1200px] mx-auto px-4 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col gap-1 p-4 rounded-lg border border-transparent hover:border-border transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary">
                    grid_on
                  </span>
                  <p className="text-muted-foreground text-sm font-medium">
                    Pixels Placed
                  </p>
                </div>
                <p className="text-3xl font-bold leading-tight">
                  {stats.pixelsPlaced.value}
                </p>
                <p className="text-green-500 text-sm font-medium flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">
                    trending_up
                  </span>{' '}
                  {stats.pixelsPlaced.trend}
                </p>
              </div>
              <div className="flex flex-col gap-1 p-4 rounded-lg border border-transparent hover:border-border transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary">
                    groups
                  </span>
                  <p className="text-muted-foreground text-sm font-medium">
                    Active Artists
                  </p>
                </div>
                <p className="text-3xl font-bold leading-tight">
                  {stats.activeArtists.value}
                </p>
                <p className="text-green-500 text-sm font-medium flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">
                    person_add
                  </span>{' '}
                  {stats.activeArtists.trend}
                </p>
              </div>
              <div className="flex flex-col gap-1 p-4 rounded-lg border border-transparent hover:border-border transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary">
                    palette
                  </span>
                  <p className="text-muted-foreground text-sm font-medium">
                    Total Canvases
                  </p>
                </div>
                <p className="text-3xl font-bold leading-tight">
                  {stats.totalCanvases.value}
                </p>
                <p className="text-green-500 text-sm font-medium flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">
                    add_circle
                  </span>{' '}
                  {stats.totalCanvases.trend}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="w-full px-4 py-20 max-w-[1200px]">
          <div className="flex flex-col gap-10 md:gap-16">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
              <div className="flex flex-col gap-4 max-w-[600px]">
                <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                  How It Works
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Experience a unique blend of creativity and strategy. Canvases
                  are shared, but your pixels are precious.
                </p>
              </div>
              <a
                className="text-primary font-bold flex items-center gap-1 hover:gap-2 transition-all"
                href="#gameplay"
              >
                Learn more about gameplay{' '}
                <span className="material-symbols-outlined text-sm">
                  arrow_forward
                </span>
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                  <span className="material-symbols-outlined text-3xl">
                    sync
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-xl font-bold leading-tight">
                    Real-time Collaboration
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Watch the canvas evolve live as users place pixels
                    simultaneously from around the globe. No refreshing needed.
                  </p>
                </div>
              </div>
              {/* Feature 2 */}
              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                  <span className="material-symbols-outlined text-3xl">
                    timer
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-xl font-bold leading-tight">
                    Daily Pixel Allowance
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Strategic placement matters. Your pixels regenerate over
                    time, making every placement a tactical decision.
                  </p>
                </div>
              </div>
              {/* Feature 3 */}
              <div className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform duration-300">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-2">
                  <span className="material-symbols-outlined text-3xl">
                    all_inclusive
                  </span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-xl font-bold leading-tight">
                    Multiple Boards
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Create and explore multiple shared canvases. Each board is a
                    unique space for collaborative art.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gameplay Section */}
        <div
          id="gameplay"
          className="w-full border-y border-border py-20"
        >
          <div className="max-w-[1200px] mx-auto px-4 flex flex-col gap-12">
            <div className="flex flex-col items-center text-center gap-4">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                Gameplay Guide
              </h2>
              <p className="text-muted-foreground max-w-[700px] text-lg">
                Master the art of collaborative pixel placement. Learn how to
                make the most of your daily quota and create stunning artworks
                together.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pixel Quota System */}
              <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-8">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-3xl">
                      inventory_2
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold leading-tight">
                    Pixel Quota System
                  </h3>
                </div>
                <div className="flex flex-col gap-4 text-slate-400">
                  <p className="leading-relaxed">
                    Every user receives a daily allocation of pixels to place on
                    shared canvases. This quota system ensures fair
                    participation and adds strategic depth to your creative
                    decisions.
                  </p>
                  <ul className="flex flex-col gap-3 list-none">
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>Your pixel quota refreshes daily at midnight</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>
                        Each pixel placement consumes one from your quota
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>
                        Plan your placements strategically - pixels are limited!
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Real-time Collaboration */}
              <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-8">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-3xl">
                      groups
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold leading-tight">
                    Real-time Collaboration
                  </h3>
                </div>
                <div className="flex flex-col gap-4 text-slate-400">
                  <p className="leading-relaxed">
                    Work alongside other artists in real-time. See pixels appear
                    instantly as they're placed, creating a dynamic and
                    interactive creative experience.
                  </p>
                  <ul className="flex flex-col gap-3 list-none">
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>
                        Watch the canvas evolve live without refreshing
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>
                        Coordinate with others to create larger artworks
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        check_circle
                      </span>
                      <span>
                        View modification history to see how art evolves
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Strategy Tips */}
              <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-8">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-3xl">
                      lightbulb
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold leading-tight">
                    Strategy Tips
                  </h3>
                </div>
                <div className="flex flex-col gap-4 text-slate-400">
                  <p className="leading-relaxed">
                    Make every pixel count with these strategic approaches to
                    collaborative art creation.
                  </p>
                  <ul className="flex flex-col gap-3 list-none">
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        tips_and_updates
                      </span>
                      <span>
                        Start with an outline before filling in details
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        tips_and_updates
                      </span>
                      <span>
                        Communicate with other artists for coordinated designs
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        tips_and_updates
                      </span>
                      <span>
                        Save pixels for important details and finishing touches
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Board Features */}
              <div className="flex flex-col gap-6 rounded-xl border border-border bg-card p-8">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <span className="material-symbols-outlined text-3xl">
                      dashboard
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold leading-tight">
                    Board Features
                  </h3>
                </div>
                <div className="flex flex-col gap-4 text-slate-400">
                  <p className="leading-relaxed">
                    Explore the full range of features available on each canvas
                    to enhance your creative experience.
                  </p>
                  <ul className="flex flex-col gap-3 list-none">
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        palette
                      </span>
                      <span>
                        Full color palette with precise color selection
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        history
                      </span>
                      <span>
                        View complete modification history and timeline
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="material-symbols-outlined text-primary text-xl mt-0.5">
                        zoom_in
                      </span>
                      <span>Zoom and pan controls for detailed work</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Getting Started CTA */}
            <div className="flex flex-col items-center gap-6 pt-8">
              <h3 className="text-2xl font-bold text-center">
                Ready to Start Creating?
              </h3>
              <p className="text-slate-400 text-center max-w-[600px]">
                Join thousands of artists and start placing your first pixels
                today. Every masterpiece begins with a single pixel.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                {user ? (
                  <button
                    onClick={handleViewMyBoards}
                    className="flex min-w-[160px] cursor-pointer items-center justify-center gap-2 rounded-lg h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-bold transition-all"
                  >
                    <span className="material-symbols-outlined text-lg">
                      dashboard
                    </span>
                    <span>Go to My Boards</span>
                  </button>
                ) : (
                  <>
                    <div className="flex min-w-[160px] items-center justify-center">
                      <LoginButton />
                    </div>
                    <button
                      onClick={handleExploreAsGuest}
                      className="flex min-w-[160px] cursor-pointer items-center justify-center rounded-lg h-12 px-6 bg-card border border-border hover:border-primary text-foreground text-base font-bold transition-all"
                    >
                      <span>Explore as Guest</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Gallery/Showcase Section */}
        <div className="w-full bg-gradient-to-b from-muted to-card border-y border-border py-20">
          <div className="max-w-[1200px] mx-auto px-4 flex flex-col gap-12">
            <div className="flex flex-col items-center text-center gap-4">
              <h2 className="text-3xl md:text-4xl font-bold leading-tight tracking-tight bg-gradient-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent dark:from-white dark:via-slate-200 dark:to-slate-300">
                Community Masterpieces
              </h2>
              <p className="text-muted-foreground max-w-[600px]">
                Check out some of the most impressive collaborative artworks
                completed by our community this month.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.keys(FEATURED_BOARD_IDS).map((boardName) => {
                const featuredBoard = featuredBoards[boardName];
                const board = featuredBoard?.board;
                const isLoading = featuredBoard?.loading ?? true;

                if (isLoading) {
                  return (
                    <div
                      key={boardName}
                      className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card"
                    >
                      <div className="aspect-video w-full overflow-hidden bg-card flex items-center justify-center">
                        <div className="text-muted-foreground text-sm">Loading...</div>
                      </div>
                      <div className="p-5 flex flex-col gap-3 bg-card">
                        <div className="h-6 bg-muted rounded animate-pulse"></div>
                        <div className="h-4 bg-muted rounded animate-pulse w-2/3"></div>
                      </div>
                    </div>
                  );
                }

                if (!board) {
                  return null;
                }

                const displayName = board.name.charAt(0).toUpperCase() + board.name.slice(1);
                const pixelCount = board.maxPixels.toLocaleString();

                return (
                  <div
                    key={boardName}
                    onClick={() => navigate(`/board/${board.name}`)}
                    className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card hover:border-primary/50 transition-all cursor-pointer"
                  >
                    <div className="w-full bg-muted/50 flex items-center justify-center aspect-video relative">
                      <canvas
                        ref={(el) => {
                          canvasRefs.current[boardName] = el;
                          if (el && featuredBoard?.board && featuredBoard?.pixels) {
                            renderBoardPreview(featuredBoard.board, featuredBoard.pixels, boardName);
                          }
                        }}
                        className="w-full h-full object-cover"
                        style={{
                          imageRendering: 'pixelated',
                          display: featuredBoard?.pixels ? 'block' : 'none',
                        }}
                      />
                      {!featuredBoard?.pixels && (
                        <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm">
                          {featuredBoard?.loading ? 'Loading...' : board ? `${board.width} × ${board.height}` : ''}
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex flex-col gap-3 bg-card">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-lg text-foreground">
                          {displayName}
                        </h3>
                        <span className="text-xs font-mono bg-primary/20 text-primary px-2 py-1 rounded">
                          {pixelCount} pixels
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="material-symbols-outlined text-base text-primary">
                          person
                        </span>
                        <span>by {board.ownerUsername}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => {
                  logButtonClick('View Gallery', 'Landing Page');
                  navigate('/browse');
                }}
                className="flex min-w-[120px] cursor-pointer items-center justify-center rounded-lg h-10 px-6 border border-primary/40 bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/20 hover:border-primary/50 text-primary text-sm font-bold transition-all"
              >
                View Gallery
              </button>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {!user && (
          <div className="w-full px-4 py-24 max-w-[800px] text-center">
            <div className="flex flex-col items-center gap-8">
              <h2 className="text-4xl md:text-5xl font-black leading-tight tracking-tight">
                Ready to leave your mark?
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-[600px]">
                The canvas is waiting. Login now to claim your first set of
                pixels and start creating with the community.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <div className="flex w-full sm:w-auto min-w-[160px] items-center justify-center">
                  <LoginButton />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};
